/**
 * The table style library, as this person sees it.
 *
 * Unlike the house styles, which are fetched when somebody opens the charter,
 * this one is needed wherever a table is drawn: a table follows its style by
 * id, so no table can be drawn right before the library is in. It is loaded
 * when a report opens, and again each time somebody opens the library, since
 * it is shared and changed by others.
 *
 * Each style says whether it is this person's, since only theirs can be changed
 * or deleted (the server says so too, and has the last word).
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as api from '@/api/tableStyles'
import { ApiError, NetworkError } from '@/api/http'
import { t } from '@/i18n'
import type { TableLook } from '@/sections/table/look'
import type { TableStyle } from '@/sections/table/tableStyle'
import { useSessionStore } from '@/stores/session'

export const useTableStylesStore = defineStore('tableStyles', () => {
  const session = useSessionStore()

  const styles = ref<TableStyle[]>([])
  const loaded = ref(false)
  const loading = ref(false)
  const busy = ref(false)
  const problem = ref<string | null>(null)

  /** The style tables follow when they name none, if the library has one. */
  const defaultStyle = computed(() => styles.value.find((style) => style.isDefault))

  function isMine(style: TableStyle): boolean {
    return session.owns(style.owner)
  }

  function describe(error: unknown): string {
    if (error instanceof ApiError) return error.message
    if (error instanceof NetworkError) return t('common.unreachable')
    return t('common.problem')
  }

  async function load(): Promise<void> {
    loading.value = true
    problem.value = null
    try {
      styles.value = await api.listTableStyles()
      loaded.value = true
    } catch (error) {
      problem.value = describe(error)
    } finally {
      loading.value = false
    }
  }

  /** Run one change to the library, saying what went wrong if it did. */
  async function change<T>(work: () => Promise<T>): Promise<T | null> {
    busy.value = true
    problem.value = null
    try {
      return await work()
    } catch (error) {
      problem.value = describe(error)
      return null
    } finally {
      busy.value = false
    }
  }

  /** Read the library again after a change that can touch several styles (the default moves). */
  async function refresh(): Promise<void> {
    try {
      styles.value = await api.listTableStyles()
    } catch (error) {
      problem.value = describe(error)
    }
  }

  async function create(
    name: string,
    look: TableLook,
    isDefault = false,
  ): Promise<TableStyle | null> {
    const created = await change(() => api.createTableStyle(name, look, isDefault))
    if (created) await refresh()
    return created
  }

  async function update(
    id: number,
    changes: { name?: string; look?: TableLook; isDefault?: boolean },
  ): Promise<TableStyle | null> {
    const updated = await change(() => api.updateTableStyle(id, changes))
    if (updated) await refresh()
    return updated
  }

  async function remove(id: number): Promise<boolean> {
    const deleted = await change(() => api.deleteTableStyle(id))
    if (deleted) styles.value = styles.value.filter((style) => style.id !== id)
    return deleted === true
  }

  return {
    styles,
    loaded,
    loading,
    busy,
    problem,
    defaultStyle,
    isMine,
    load,
    create,
    update,
    remove,
  }
})
